import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import { PageStatusCircle } from '@/components/PageStatusCircle';
import { BlockPath } from '../../../editor/PageEditor/CurrentBlockProvider';
import { useControlledFieldArray } from '../../../hooks/useControlledFieldArray';
import { BlockSpecificPage } from '../../../editor/lib/declarations/BlockSpecificPage';
import { HeaderMenuBlock } from '../../../declarations/models/blocks/settingsBlocks/HeaderMenuBlock';
import { LinkType } from '../../../declarations/models/LinkType';
import Links from '../../../components/Links/Links';
import { Page } from '../../../declarations/models/Page';
import { LinkExternal } from '../../../declarations/models/LinkExternal';
import Container from '../../../components/Container';
import Styles from '../../../assets/js/Styles';
import { Draggable } from '../../../declarations/Draggable';
import TextInput from '../../../components/forms/TextInput';
import LinkListItem from '../../../components/Links/LinkListItem';
import SwitchInput from '../../../components/forms/SwitchInput';

export interface LanguageMenuMainMenuSubMenuSettingsProps {
  itemPath: `${BlockPath}.items.${number}`;
}

export const LanguageMenuMainMenuSubMenuSettings: FC<LanguageMenuMainMenuSubMenuSettingsProps> = ({ itemPath }) => {
  const { t } = useTranslation('components');
  const { t: tCommon } = useTranslation('common');

  const {
    fields: items,
    append,
    update,
    move,
    remove,
  } = useControlledFieldArray<BlockSpecificPage<HeaderMenuBlock>, `${BlockPath}.items.${number}.items`>({
    name: `${itemPath}.items`,
  });

  const [itemVisible, setItemVisible] = useState<boolean>(true);

  const handleMove = (fromIndex: number, toIndex: number) => {
    // Its critical that settings are hidden during move to prevent rendering issues!!!!!!!!!!!!!!!!!!!!!!!!!!
    setItemVisible(false);
    move(fromIndex, toIndex);
    setTimeout(() => {
      // Re-show settings after move is done
      setItemVisible(true);
    }, 200);
  };

  const handleRemove = (index: number) => {
    setItemVisible(false);
    remove(index);
    setTimeout(() => {
      // Re-show settings after move is done
      setItemVisible(true);
    }, 0);
  };

  return (
    <Container
      fullWidth
      left
      p={2}
      column
      sx={{
        backgroundColor: Styles.Colors.THEME_BG_COLOR,
        borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
      }}>
      <Typography>{t('Settings.LanguageSettings.LanguageMenuSettings.subMenu')}</Typography>
      {(items as unknown as Array<Page | LinkExternal>)?.map((item, i) => (
        <LinkListItem
          key={`${item.id}`}
          linkType={item.type as LinkType}
          type={Draggable.OTHER}
          index={i}
          onReorder={handleMove}
          onDelete={handleRemove}
          itemsArrayName='items'
          showLinkDescriptionField={false}
          onChangeFile={() => null}
          blockPath={itemPath}
          iconOverride={'status' in item ? <PageStatusCircle status={item.status} size={20} /> : undefined}
          headerSubTitle={<>{item.hidden && <Typography variant='overline'>({tCommon('hidden')})</Typography>}</>}
          color={Styles.Colors.LIGHT_GREY}>
          <Container fullWidth p={2} column>
            {itemVisible && (
              <>
                <Container fullWidth>
                  <TextInput
                    path={`${itemPath}.items.${i}.title`}
                    label={t('Settings.LanguageSettings.LanguageMenuSettings.menuPointTitle')}
                    defaultValue=''
                  />
                  <TextInput
                    path={`${itemPath}.items.${i}.anchor`}
                    label={t('LinkListItem.Anchor')}
                    defaultValue=''
                    InputProps={{
                      startAdornment: <Typography color={Styles.Colors.MEDIUM_GREY}>#</Typography>,
                    }}
                  />
                  <TextInput
                    path={`${itemPath}.items.${i}.cssClass`}
                    label={t('Settings.LanguageSettings.LanguageMenuSettings.cssClass')}
                    defaultValue=''
                  />
                  <SwitchInput path={`${itemPath}.items.${i}.hidden`} label={tCommon('hide')} defaultValue={false} />
                </Container>
                <Container fullWidth>
                  {item.type === LinkType.LINK && (
                    <TextInput
                      path={`${itemPath}.items.${i}.url`}
                      label={t('Settings.LanguageSettings.LanguageMenuSettings.url')}
                      defaultValue=''
                    />
                  )}
                </Container>
              </>
            )}
          </Container>
        </LinkListItem>
      ))}
      <Links
        onUpdate={update}
        onAppend={append}
        onRemove={remove}
        onReorder={move}
        showLinkItems={false}
        showLinkDescriptionField={false}
        showLinkStyleSelector={false}
        showListChildrenInput={false}
        linkItems={items as unknown as Array<Page | LinkExternal>}
        linkTypes={[LinkType.PAGE, LinkType.LINK]}
        blockPath={itemPath}
      />
    </Container>
  );
};

export default LanguageMenuMainMenuSubMenuSettings;
